import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../app/AuthContext";
import type { CatalogHit, DocumentAnalysisRead, DocumentAnalysisRow, DocumentAnalysisRelation } from "../../shared/api/documentTypes";
import type { Page } from "../../shared/api/types";
import { collectPermissions } from "../../shared/lib/permissions";
import { Button, Callout, Field, Input, Spinner } from "../../shared/ui/Primitives";

const types: Record<string, string> = {
  specification: "Спецификация", document_list: "Перечень КД", detail_drawing: "Чертёж детали",
  assembly_drawing: "Сборочный чертёж", technical_text: "Текстовый технический документ", unknown: "Тип не установлен",
};
const sections: Record<string, string> = {
  documentation: "Документация", assemblies: "Сборочные единицы", details: "Детали",
  standard_products: "Стандартные изделия", other_products: "Прочие изделия", materials: "Материалы",
  kits: "Комплекты", unknown: "Раздел не установлен",
};
const relations: Record<string, string> = {
  mentions: "Упоминание", specification_item: "Позиция спецификации", includes_document: "Включён в перечень",
  requires_drawing: "Чертёж требуется по настроенному правилу",
};
const statuses: Record<string, string> = {
  queued: "В очереди", running: "Читаем PDF", completed: "Обработка завершена", partial: "Требуется проверка", failed: "Ошибка обработки",
};
const message = (error: unknown) => error instanceof Error ? error.message : "Не удалось получить результаты";
const warningNames: Record<string, string> = {
  unreadable_page: "Страница не прочитана", page_extraction_failed: "Ошибка чтения страницы",
  ocr_without_geometry: "OCR вернул текст без координат таблицы", no_supported_rows: "Не удалось восстановить строки",
  uncertain_classification: "Тип документа неоднозначен", unsupported_table: "Неизвестная структура таблицы",
  damaged_or_empty_row: "Повреждённая или пустая строка", table_extraction_failed: "Не удалось извлечь таблицу",
  unclear_designation: "Нечёткое обозначение", mixed_alphabets: "Смешаны похожие латинские и русские буквы",
  suspicious_numeric_glyphs: "В числовой части обозначения обнаружены похожие буквы",
  conflicting_values: "Конфликт наименования или количества", multiple_readings: "Несколько возможных прочтений",
  inferred_continuation_columns: "Колонки продолжения определены по предыдущей странице",
  designation_pattern_mismatch: "Обозначение не соответствует поддерживаемому шаблону",
  unmatched_corrections: "Есть сохранённые исправления специалиста, которые нужно заново привязать к строкам",
};
function effective(row: DocumentAnalysisRow, field: "designation_original" | "name" | "quantity" | "section") {
  return (Object.hasOwn(row.effective_values, field) ? row.effective_values[field] : row[field]) || "—";
}

export function DocumentAnalysisPanel() {
  const { api, user } = useAuth();
  const permissions = useMemo(() => collectPermissions(user), [user]);
  const [query, setQuery] = useState("");
  const [hits, setHits] = useState<Page<CatalogHit> | null>(null);
  const [run, setRun] = useState<DocumentAnalysisRead | null>(null);
  const [filename, setFilename] = useState("");
  const [rows, setRows] = useState<Page<DocumentAnalysisRow> | null>(null);
  const [links, setLinks] = useState<Page<DocumentAnalysisRelation> | null>(null);
  const [rowOffset, setRowOffset] = useState(0);
  const [linkOffset, setLinkOffset] = useState(0);
  const [selected, setSelected] = useState<{ page: number; source_fragment: string; warnings?: string[] } | null>(null);
  const [pdf, setPdf] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const active = run?.status === "queued" || run?.status === "running";
  const settled = run?.status === "completed" || run?.status === "partial";

  useEffect(() => {
    if (!run?.id || !active) return;
    let alive = true;
    const timer = setInterval(() => {
      void api.getDocumentAnalysis(run.id).then(value => { if (alive) setRun(value); })
        .catch(error => { if (alive) setError(message(error)); });
    }, 4000);
    return () => { alive = false; clearInterval(timer); };
  }, [api, run?.id, active]);

  useEffect(() => {
    if (!run?.id || !settled) { setRows(null); setLinks(null); return; }
    let alive = true;
    void Promise.all([api.getDocumentAnalysisRows(run.id, rowOffset), api.getDocumentAnalysisRelations(run.id, linkOffset)])
      .then(([r, l]) => { if (alive) { setRows(r); setLinks(l); } })
      .catch(error => { if (alive) setError(message(error)); });
    return () => { alive = false; };
  }, [api, run?.id, settled, rowOffset, linkOffset]);

  useEffect(() => {
    setPdf("");
    if (!run?.id || !settled) return;
    let alive = true; let url = "";
    void api.downloadDocumentAnalysisPdf(run.id).then(file => {
      if (alive) { url = URL.createObjectURL(file.blob); setPdf(url); }
    }).catch(error => { if (alive) setError(message(error)); });
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [api, run?.id, settled]);

  async function act(action: () => Promise<void>) {
    setBusy(true); setError("");
    try { await action(); } catch (error) { setError(message(error)); }
    finally { setBusy(false); }
  }
  function choose(value: DocumentAnalysisRead, sourceName?: string) {
    if (value.id !== run?.id) { setRows(null); setLinks(null); }
    if (sourceName) setFilename(sourceName);
    setRun(value); setSelected(null); setRowOffset(0); setLinkOffset(0);
  }
  if (!permissions.has("documents.view")) return null;
  return <details className="kd-upload">
    <summary>Чтение PDF из каталога документации</summary>
    <form className="stack" onSubmit={event => { event.preventDefault(); void act(async () => {
      setHits(await api.searchDocuments(query));
    }); }}>
      <Field label="Обозначение" hint="Ищем в подключённом каталоге. Совпадения могут быть ссылками внутри другого документа.">
        <Input value={query} onChange={event => setQuery(event.target.value)} placeholder="Стал.115003" maxLength={250} />
      </Field>
      <Button type="submit" disabled={busy || query.trim().length < 3}>Найти документы</Button>
    </form>
    {error ? <Callout tone="warning">{error}</Callout> : null}
    {hits ? <><p>Найдено: {hits.total}. Показано: {hits.items.length}.</p>
      {hits.items.map((hit, index) => <div key={`${hit.file_id}-${index}`} className="section-title-row">
        <span>{hit.filename} · {hit.match_basis === "text" ? "ссылка в тексте" : hit.match_basis === "filename" ? "имя файла" : "метаданные"}
          {hit.match_type === "candidate" ? " · кандидат" : ""}{hit.ambiguous ? " · есть другие совпадения" : ""}</span>
        <Button size="small" variant="secondary" disabled={busy || active} onClick={() => void act(async () => {
          const history = await api.documentAnalysisHistory(hit.file_id);
          if (history.items[0]) choose(history.items[0], hit.filename); else setError("Этот PDF ещё не анализировался.");
        })}>Результаты</Button>
        {permissions.has("documents.analyze") ? <Button size="small" disabled={busy || active} onClick={() => void act(async () => {
          choose(await api.startDocumentAnalysis(hit.file_id), hit.filename);
        })}>Прочитать PDF</Button> : null}
      </div>)}</> : null}
    {run ? <>
      <p>{filename} · {statuses[run.status]} · версия анализа {run.revision}</p>
      {active ? <Spinner label="Извлекаем структуру документа" /> : null}
      {run.status === "failed" ? <Callout tone="warning">PDF не удалось обработать. Проверьте доступность и состояние исходного файла.</Callout> : null}
      {settled ? <>
        <p>Предполагаемый тип: {types[run.classification.document_type ?? "unknown"]}. Это результат распознавания, требующий сверки.</p>
        <p>Строк: {rows?.total ?? 0}; отношений: {links?.total ?? 0}; сомнительных строк: {run.summary.review_rows ?? 0}.</p>
        {run.warnings.length ? <Callout tone="warning"><p>Ограничения распознавания:</p><ul>{run.warnings.map((warning, index) => <li key={index}>
          {warning.page ? `Страница ${warning.page}: ` : ""}{warningNames[warning.code] ?? "Требуется дополнительная проверка результата"}
        </li>)}</ul></Callout> : null}
        <details><summary>Основания классификации</summary><pre>{JSON.stringify(run.classification.basis, null, 2)}</pre></details>
        <h4>Распознанные строки</h4>
        <div style={{ overflowX: "auto" }}><table><thead><tr><th>Раздел</th><th>Обозначение</th><th>Наименование</th><th>Кол.</th><th>Страница</th><th>Проверка</th></tr></thead>
          <tbody>{rows?.items.map(row => <tr key={row.id}>
            <td>{sections[effective(row, "section")] ?? "Другой раздел"}</td><td>{effective(row, "designation_original")}</td>
            <td>{effective(row, "name")}</td><td>{effective(row, "quantity")}</td>
            <td><Button size="small" variant="ghost" onClick={() => setSelected(row)}>{row.page}</Button></td>
            <td>{row.correction_id ? "Есть исправление специалиста" : row.requires_review ? "Требуется" : "Автоматическое извлечение"}
              {row.variants.length ? ` · варианты: ${row.variants.join(" / ")}` : ""}</td>
          </tr>)}</tbody></table></div>
        <Button size="small" variant="ghost" disabled={!rowOffset} onClick={() => setRowOffset(Math.max(0, rowOffset - 30))}>Предыдущие строки</Button>
        <Button size="small" variant="ghost" disabled={!rows || rowOffset + 30 >= rows.total} onClick={() => setRowOffset(rowOffset + 30)}>Следующие строки</Button>
        <h4>Предполагаемые отношения</h4><p>Наличие соответствующего файла ещё не проверено.</p>
        {links?.items.map(link => <p key={link.id}><Button size="small" variant="ghost" onClick={() => setSelected(link)}>Стр. {link.page}</Button> {link.designation_original} · {relations[link.relation_type]}{link.effective_requires_review ? " · требуется проверка" : ""}{link.correction_id ? " · исходная строка исправлена специалистом" : ""}</p>)}
        <Button size="small" variant="ghost" disabled={!linkOffset} onClick={() => setLinkOffset(Math.max(0, linkOffset - 30))}>Предыдущие отношения</Button>
        <Button size="small" variant="ghost" disabled={!links || linkOffset + 30 >= links.total} onClick={() => setLinkOffset(linkOffset + 30)}>Следующие отношения</Button>
        {selected ? <details open><summary>Исходный фрагмент, страница {selected.page}</summary><pre style={{ whiteSpace: "pre-wrap" }}>{selected.source_fragment}</pre>
          {selected.warnings?.map((code, index) => <p key={index}>{warningNames[code] ?? "Требуется проверка извлечения"}</p>)}</details> : null}
        {pdf ? <><a href={`${pdf}#page=${selected?.page ?? 1}`} target="_blank" rel="noopener noreferrer">Открыть исходный PDF</a>
          <iframe src={`${pdf}#page=${selected?.page ?? 1}`} title="Исходный PDF для сверки распознавания" style={{ width: "100%", height: 520, border: "1px solid #ddd" }} /></> : null}
        {permissions.has("documents.analyze") ? <Button variant="secondary" disabled={busy} onClick={() => void act(async () => {
          choose(await api.startDocumentAnalysis(run.file_id, true));
        })}>Повторить распознавание</Button> : null}
      </> : null}
    </> : null}
  </details>;
}
